// ui/src/components/finance/CompFinancialControl.jsx
/**
 * @file CompFinancialControl.jsx
 * @module ui/src/components/finance/CompFinancialControl
 * @description
 *   Period-level Finance orchestration surface showing server-provided compensation
 *   aggregates and routing users into plan drill-down, close workflow, and settlement
 *   ledger components. It owns navigation state only and performs no financial math.
 *
 * @version 1.0.0
 * @since S756
 * @author data-fit LLC · DecisionFlow · S776
 *
 * @architecture
 *   Locked decisions : #1, #25
 *   Temporal invariants : INV-1, INV-3
 *   Design principles : DP-1, DP-4, DP-5, DP-14
 *   Cross-cutting contracts : XC-5, XC-6, XC-7
 *
 * @domain-rules
 *   - Period status is displayed solely as workflow context and never determines
 *     plan-version applicability.
 *   - Financial KPI values preserve available, not-available, and partial states.
 *   - Settlement adjustment, amendment count, and close status remain separate from
 *     calculated payout and are never collapsed into a synthetic health score.
 *   - Plan payout drill-down, close workflow, and settlement behavior are delegated
 *     to C-57, C-31, and C-33 respectively.
 *
 * @session-6-migration-flags
 *   - GET /api/v1/finance/period/{id}/summary — Session 6 API gap
 *   - plan.effective_from / plan.effective_to — pending m_tc_01
 *
 * @changelog
 *   1.0.0  S756  Initial build — Group Finance batch delivery
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import FinanceCompDetail from './FinanceCompDetail';
import PeriodCloseWorkflow from './PeriodCloseWorkflow';
import SettlementLedger from './SettlementLedger';

/**
 * @typedef {Object} PeriodPlanRow
 * @property {number} planId
 * @property {string} planCode
 * @property {string} planName
 * @property {number} planVersion
 * @property {number} positionCount
 * @property {number|null} projectedPayout
 * @property {number|null} calculatedPayout
 * @property {'available'|'not_available'|'partial'} payoutState
 * @property {boolean} hasSplitPeriod
 * @property {boolean} hasOpenExceptions
 */

/**
 * @typedef {Object} CompFinancialControlProps
 * @property {number} fiscalPeriodId
 * @property {Object} currentUser
 */

const FINANCIAL_STATES = ['available', 'not_available', 'partial'];

const styles = {
  tabs: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
  kpis: {
    display: 'grid',
    gap: '0.75rem',
    gridTemplateColumns: 'repeat(auto-fit, minmax(11rem, 1fr))',
  },
  kpi: {
    background: 'rgb(var(--color-surface))',
    border: '1px solid rgb(var(--color-border))',
    borderRadius: '0.5rem',
    padding: '0.75rem',
  },
  label: {
    color: 'rgb(var(--color-text-muted))',
    display: 'block',
    fontSize: '0.75rem',
    fontWeight: 600,
    marginBottom: '0.25rem',
  },
  value: {
    fontSize: '1.125rem',
    fontVariantNumeric: 'tabular-nums',
    fontWeight: 600,
  },
  controlLines: {
    background: 'rgb(var(--color-surface-muted))',
    border: '1px solid rgb(var(--color-border))',
    borderRadius: '0.5rem',
    display: 'grid',
    gap: '0.5rem',
    gridTemplateColumns: 'repeat(auto-fit, minmax(13rem, 1fr))',
    padding: '0.75rem',
  },
  numeric: {
    fontVariantNumeric: 'tabular-nums',
    textAlign: 'right',
  },
};

function formatMoney(value) {
  // WHY: Formatting does not compute economics; numeric zero remains a valid result.
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return 'Not available';
  }

  return new Intl.NumberFormat(undefined, {
    currency: 'USD',
    style: 'currency',
  }).format(value);
}

function FinancialStateValue({ value, state }) {
  // WHY: Partial financial coverage must not be presented as a complete amount.
  if (state === 'partial') {
    return (
      <span className="modifier-manager-badge modifier-manager-badge--incomplete">
        Incomplete / partial
      </span>
    );
  }

  // WHY: Missing economic result is explicitly different from a calculated zero.
  if (state === 'not_available') {
    return <span>Not available</span>;
  }

  // WHY: Available/null is not silently coerced to zero.
  if (value === null || value === undefined) {
    return <span>Not available</span>;
  }

  return <span>{formatMoney(value)}</span>;
}

FinancialStateValue.propTypes = {
  state: PropTypes.oneOf(FINANCIAL_STATES).isRequired,
  value: PropTypes.number,
};

FinancialStateValue.defaultProps = {
  value: null,
};

function CompFinancialControl({
  fiscalPeriodId,
  currentUser,
}) {
  // WHY: periodSummary is the authoritative orchestration payload; no KPI is calculated here.
  const [periodSummary, setPeriodSummary] = useState(null);

  // WHY: selectedPlanId exists only to route to C-57 without taking ownership of drill logic.
  const [selectedPlanId, setSelectedPlanId] = useState(null);

  // WHY: activeView controls navigation among independent Finance surfaces.
  const [activeView, setActiveView] = useState('summary');

  // WHY: Request state is separate from financial availability state.
  const [loading, setLoading] = useState(false);

  // WHY: Endpoint failure must not be mistaken for zero or partial compensation.
  const [error, setError] = useState('');

  useEffect(() => {
    const controller = new AbortController();

    async function loadPeriodSummary() {
      setLoading(true);
      setError('');

      try {
        // WHY: Period aggregates and plan rows are server-supplied Finance results;
        // orchestration must never derive them from lower-level records.
        const response = await fetch(
          `/api/v1/finance/period/${fiscalPeriodId}/summary`,
          {
            method: 'GET',
            headers: { Accept: 'application/json' },
            signal: controller.signal,
          },
        );

        if (!response.ok) {
          throw new Error(
            `Finance period summary failed (${response.status}).`,
          );
        }

        const payload = await response.json();
        setPeriodSummary(payload);
      } catch (requestError) {
        if (requestError.name !== 'AbortError') {
          setError(
            requestError.message || 'Finance period summary could not be loaded.',
          );
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }

    loadPeriodSummary();

    // WHY: Changing fiscal period cancels an obsolete aggregate request.
    return () => controller.abort();
  }, [fiscalPeriodId]);

  useEffect(() => {
    // WHY: Plan drill context cannot carry across fiscal periods because each period
    // has distinct server-provided financial evidence.
    setSelectedPlanId(null);
    setActiveView('summary');
  }, [fiscalPeriodId]);

  const handleViewChange = (nextView) => {
    // WHY: Tab navigation controls orchestration only; no domain state is recomputed.
    setSelectedPlanId(null);
    setActiveView(nextView);
  };

  const handleViewPlan = (planId) => {
    // WHY: Selecting a plan delegates financial evidence rendering to C-57.
    setSelectedPlanId(planId);
    setActiveView('drill_down');
  };

  const handleBackToSummary = () => {
    // WHY: Breadcrumb navigation simply removes the child drill selection.
    setSelectedPlanId(null);
    setActiveView('summary');
  };

  // WHY: Loading is a transport state, not financial incompleteness.
  if (loading && !periodSummary) {
    return (
      <div
        aria-live="polite"
        className="modifier-manager-card"
        role="status"
      >
        <div className="modifier-manager-body">
          <div className="skeleton">Loading financial control summary…</div>
        </div>
      </div>
    );
  }

  // WHY: API failure is not represented as a financial zero or health score.
  if (error && !periodSummary) {
    return (
      <div className="modifier-manager-error-state" role="alert">
        {error}
      </div>
    );
  }

  if (!periodSummary) {
    return (
      <div className="modifier-manager-empty-state" role="status">
        Financial control data is not available.
      </div>
    );
  }

  const aggregate = periodSummary.periodAggregate || {};
  const periodFinancialState =
    aggregate.payoutState
    || aggregate.financialState
    || 'available';

  return (
    <section
      aria-labelledby="comp-financial-control-title"
      style={{ fontFamily: '"IBM Plex Sans", "Segoe UI", Arial, sans-serif' }}
    >
      <div className="modifier-manager-card">
        <div className="modifier-manager-header">
          <div>
            <h1 id="comp-financial-control-title">
              Financial control · {periodSummary.periodLabel}
            </h1>
            <div>
              <span>Period status: </span>
              <span className="modifier-manager-badge modifier-manager-badge--readonly">
                {periodSummary.periodStatus}
              </span>
            </div>
          </div>
        </div>

        <div className="modifier-manager-body">
          <div className="modifier-manager-readonly-note">
            Period status is workflow context only. It does not determine which
            plan version governs an economic event.
          </div>

          <nav aria-label="Financial control views" style={styles.tabs}>
            <button
              aria-current={activeView === 'summary' ? 'page' : undefined}
              className={
                activeView === 'summary'
                  ? 'primary-button'
                  : 'secondary-button'
              }
              onClick={() => handleViewChange('summary')}
              type="button"
            >
              Summary
            </button>

            <button
              aria-current={
                activeView === 'close_workflow' ? 'page' : undefined
              }
              className={
                activeView === 'close_workflow'
                  ? 'primary-button'
                  : 'secondary-button'
              }
              onClick={() => handleViewChange('close_workflow')}
              type="button"
            >
              Close Workflow
            </button>

            <button
              aria-current={
                activeView === 'settlement' ? 'page' : undefined
              }
              className={
                activeView === 'settlement'
                  ? 'primary-button'
                  : 'secondary-button'
              }
              onClick={() => handleViewChange('settlement')}
              type="button"
            >
              Settlement
            </button>
          </nav>
        </div>
      </div>

      {/*
       * WHY: Summary owns only period-level presentation and links into delegated
       * components; it does not reproduce child-domain logic.
       */}
      {activeView === 'summary' ? (
        <div className="modifier-manager-card">
          <div className="modifier-manager-header">
            <h2>Period summary</h2>
          </div>

          <div className="modifier-manager-body">
            <div style={styles.kpis}>
              <div style={styles.kpi}>
                <span style={styles.label}>Projected compensation</span>
                <div style={styles.value}>
                  <FinancialStateValue
                    state={
                      aggregate.projectedCompensationState
                      || periodFinancialState
                    }
                    value={aggregate.projectedCompensation}
                  />
                </div>
              </div>

              <div style={styles.kpi}>
                <span style={styles.label}>Calculated actual</span>
                <div style={styles.value}>
                  <FinancialStateValue
                    state={
                      aggregate.calculatedActualState
                      || periodFinancialState
                    }
                    value={aggregate.calculatedActual}
                  />
                </div>
              </div>

              <div style={styles.kpi}>
                <span style={styles.label}>Accrual</span>
                <div style={styles.value}>
                  <FinancialStateValue
                    state={
                      aggregate.accrualState
                      || periodFinancialState
                    }
                    value={aggregate.accrual}
                  />
                </div>
              </div>

              <div style={styles.kpi}>
                <span style={styles.label}>Variance</span>
                <div style={styles.value}>
                  <FinancialStateValue
                    state={
                      aggregate.varianceState
                      || periodFinancialState
                    }
                    value={aggregate.variance}
                  />
                </div>
              </div>

              <div style={styles.kpi}>
                <span style={styles.label}>Positions covered</span>
                <div style={styles.value}>
                  {aggregate.positionsCovered ?? 0}
                </div>
              </div>

              <div style={styles.kpi}>
                <span style={styles.label}>Open exceptions</span>
                <div style={styles.value}>
                  {aggregate.exceptionCount > 0 ? (
                    <span className="modifier-manager-badge modifier-manager-badge--incomplete">
                      {aggregate.exceptionCount} open
                    </span>
                  ) : (
                    <span>0</span>
                  )}
                </div>
              </div>
            </div>

            {/*
             * WHY: XC-5 requires these control dimensions to remain separate from
             * financial KPI values and from one another.
             */}
            <div style={styles.controlLines}>
              <div>
                <span style={styles.label}>Settlement adjustments</span>
                <FinancialStateValue
                  state={
                    aggregate.settlementAdjustmentState
                    || periodFinancialState
                  }
                  value={aggregate.settlementAdjustmentTotal}
                />
              </div>

              <div>
                <span style={styles.label}>
                  Amendments affecting period
                </span>
                <span>{aggregate.amendmentsAffectingPeriod ?? 0}</span>
              </div>

              <div>
                <span style={styles.label}>Close status</span>
                <span>{aggregate.closeStatus || 'Not available'}</span>
              </div>
            </div>

            <div className="modifier-manager-table-wrapper">
              <table className="modifier-manager-table">
                <thead>
                  <tr>
                    <th scope="col">Plan name / code / version</th>
                    <th scope="col">Positions</th>
                    <th scope="col">Projected payout</th>
                    <th scope="col">Calculated payout</th>
                    <th scope="col">Split period</th>
                    <th scope="col">Exceptions</th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {(periodSummary.plans || []).map((plan) => (
                    <tr key={plan.planId}>
                      <td>
                        <strong>{plan.planName}</strong>
                        <div>
                          {plan.planCode} · V{plan.planVersion}
                        </div>
                      </td>
                      <td>{plan.positionCount}</td>
                      <td style={styles.numeric}>
                        <FinancialStateValue
                          state={plan.payoutState}
                          value={plan.projectedPayout}
                        />
                      </td>
                      <td style={styles.numeric}>
                        <FinancialStateValue
                          state={plan.payoutState}
                          value={plan.calculatedPayout}
                        />
                      </td>
                      <td>
                        {plan.hasSplitPeriod ? (
                          <span className="modifier-manager-badge modifier-manager-badge--incomplete">
                            Yes
                          </span>
                        ) : (
                          <span>No</span>
                        )}
                      </td>
                      <td>
                        {plan.hasOpenExceptions ? (
                          <span className="modifier-manager-badge modifier-manager-badge--incomplete">
                            Open exceptions
                          </span>
                        ) : (
                          <span>None</span>
                        )}
                      </td>
                      <td>
                        <button
                          className="text-button"
                          onClick={() => handleViewPlan(plan.planId)}
                          type="button"
                        >
                          View detail
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : null}

      {/*
       * WHY: C-57 owns plan/position/payout drill-down and is not reimplemented here.
       */}
      {activeView === 'drill_down' && selectedPlanId !== null ? (
        <div>
          <div className="modifier-manager-footer">
            <button
              className="text-button"
              onClick={handleBackToSummary}
              type="button"
            >
              Back to summary
            </button>
          </div>

          <FinanceCompDetail
            fiscalPeriodId={fiscalPeriodId}
            planId={selectedPlanId}
            positionId={null}
          />
        </div>
      ) : null}

      {/*
       * WHY: C-31 exclusively owns close gate workflow behavior.
       */}
      {activeView === 'close_workflow' ? (
        <PeriodCloseWorkflow
          currentUser={currentUser}
          fiscalPeriodId={fiscalPeriodId}
        />
      ) : null}

      {/*
       * WHY: C-33 exclusively owns settlement lineage and its two-period contract.
       */}
      {activeView === 'settlement' ? (
        <SettlementLedger
          fiscalPeriodId={fiscalPeriodId}
          planId={null}
        />
      ) : null}
    </section>
  );
}

CompFinancialControl.propTypes = {
  currentUser: PropTypes.object.isRequired,
  fiscalPeriodId: PropTypes.number.isRequired,
};

/**
 * @example
 * <CompFinancialControl
 *   fiscalPeriodId={202608}
 *   currentUser={{
 *     userId: 'finance-user-uuid',
 *     name: 'Finance Admin',
 *   }}
 * />
 */

/**
 * DOMAIN COMPLIANCE CHECK
 *
 * ✓ C-30 orchestration boundary:
 *   Plan drill-down delegates to C-57, close workflow delegates to C-31, and
 *   settlement lineage delegates to C-33.
 *
 * ✓ XC-5:
 *   Settlement adjustment, amendments affecting period, and close status are
 *   separate control line items outside the six KPI tiles. No health score exists.
 *
 * ✓ XC-6:
 *   Financial KPI and plan-row values preserve available, not available, and
 *   incomplete/partial states without hiding numeric zero.
 *
 * ✓ INV-3:
 *   periodStatus appears solely as workflow context and never determines plan
 *   applicability or plan-version selection.
 *
 * ✓ DP-4:
 *   No ProvisionalBadge is rendered at the orchestration level.
 *
 * ✓ DP-5:
 *   Finance can move from period aggregate to persisted plan/position evidence.
 *
 * ✓ XC-7 / DP-14:
 *   Split-period and exception states are expressed in text as well as badges.
 *
 * ✓ DP-8:
 *   Prohibited Finance approval language is absent.
 */

/**
 * SESSION 6 MIGRATION FLAGS
 *
 * - GET /api/v1/finance/period/{id}/summary — Session 6 API gap.
 * - plan.effective_from / plan.effective_to — pending m_tc_01; consumed downstream
 *   in C-57 rather than inferred at this orchestration layer.
 */

/**
 * ACCESSIBILITY NOTES
 *
 * - Navigation controls are native buttons with aria-current on active views.
 * - Summary and plan lists use semantic table headings.
 * - Financial state is textual and never depends solely on color.
 * - Split-period and exception indicators contain explicit words.
 * - Request loading and failure use status/alert semantics.
 */

export default CompFinancialControl;
